import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Fade,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import BlockIcon from '@mui/icons-material/Block';
import { BASE_URL } from '../global/Server/endpoints';
import { Divider } from '@material-ui/core';

const NfcGatePage = () => {
  const [status, setStatus] = useState('processing'); // processing, pending, allowed, denied, error
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const pollTimer = useRef(null);

  // Clean up timer on unmount
  useEffect(() => () => clearInterval(pollTimer.current), []);

  const startPolling = useCallback(
    (phone, locationId) => {
      if (pollTimer.current) clearInterval(pollTimer.current);

      let attempts = 0;
      const maxAttempts = 600; // 30 minutes (600 * 3s = 1800s)

      pollTimer.current = setInterval(async () => {
        attempts++;
        if (attempts > maxAttempts) {
          clearInterval(pollTimer.current);
          setStatus('error');
          return;
        }

        try {
          const response = await fetch(
            `${BASE_URL}/security/checkStatus?phone=${phone}&locationId=${locationId}`
          );
          if (response.ok) {
            const data = await response.json();

            // Map backend status to local state
            if (data.status === 'ALLOWED') {
              setStatus('allowed');
              if (window.navigator.vibrate)
                window.navigator.vibrate([200, 100, 200]);

              setTimeout(() => {
                history.replace('/session-complete');
              }, 3000);

              clearInterval(pollTimer.current);
            } else if (data.status === 'DENIED' || data.status === 'BLOCKED') {
              setStatus('denied');
              clearInterval(pollTimer.current);
            } else if (data.status === 'PENDING') {
              setStatus('pending');
            } else if (data.status === 'NOT_FOUND') {
              // If the 30 min window expired while polling, reset to start
              setStatus('processing');
              clearInterval(pollTimer.current);
            }
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }, 3000);
    },
    [history]
  );

  const registerTap = useCallback(
    async (locationName, phone) => {
      try {
        // 1. FIRST: Check if there is an active session (Handles page refresh)
        // Note: You might need to adjust your backend checkStatus to handle locationName
        // if locationId isn't known yet.
        const checkResponse = await fetch(
          `${BASE_URL}/security/checkStatus?phone=${phone}&locationName=${locationName}`
        );
        const checkData = await checkResponse.json();

        if (checkData.status !== 'NOT_FOUND') {
          // Sync with existing status
          setStatus(checkData.status.toLowerCase());
          // If it's still pending, keep polling
          if (checkData.status === 'PENDING') {
            startPolling(phone, checkData.locationId);
          }
          return;
        }

        // 2. SECOND: If no active session, register a new tap
        const response = await fetch(
          `${BASE_URL}/security/registerGateTap?locationName=${locationName}&phone=${phone}`
        );
        const data = await response.json();

        if (response.ok) {
          setStatus('pending');
          startPolling(phone, data.locationId);
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Registration error:', error);
        setStatus('error');
      }
    },
    [startPolling]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const locationName = params.get('locationName');
    const storedPhone = localStorage.getItem('nube_customer_phone');

    if (!storedPhone) {
      history.push('/404');
      return;
    }
    if (!locationName) {
      setStatus('error');
      return;
    }

    // Only trigger registration if we are in the initial 'processing' state
    if (status === 'processing') {
      registerTap(locationName, storedPhone);
    }
  }, [location.search, history, registerTap, status]);

  return (
    <Box
      sx={{
        height: '100dvh',
        width: '100vw',
        background: 'linear-gradient(180deg, #001665 0%, #000b33 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
      }}
    >
      <Fade in timeout={800}>
        <Paper
          sx={{
            p: isSmallMobile ? 4 : 5,
            borderRadius: 8,
            textAlign: 'center',
            maxWidth: 400,
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
            maxHeight: '90dvh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {/* PENDING / PROCESSING VIEW */}
          {(status === 'processing' || status === 'pending') && (
            <Stack spacing={3} alignItems="center">
              <CheckCircleIcon
                sx={{
                  fontSize: 70,
                  color: '#00e676',
                  opacity: status === 'processing' ? 0.5 : 1,
                }}
              />
              <Typography
                variant="h5"
                sx={{ fontWeight: 800, color: '#001665' }}
              >
                {status === 'processing'
                  ? 'Initializing...'
                  : 'Entry Requested'}
              </Typography>
              <Typography variant="body1" sx={{ color: '#555', px: 2 }}>
                Your request has been sent to the security team.
              </Typography>
              <Divider sx={{ width: '100%' }} />
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#f0f4ff',
                  borderRadius: 4,
                  width: '100%',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: '#001665', fontWeight: 700 }}
                >
                  Please wait...
                </Typography>
                <Typography variant="body2" sx={{ color: '#555', mt: 0.5 }}>
                  Security will grant you entry shortly.
                </Typography>
              </Box>
            </Stack>
          )}

          {/* ALLOWED VIEW */}
          {status === 'allowed' && (
            <Stack spacing={3} alignItems="center">
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 140,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: '#00e676',
                    animation: 'pulse 2.5s infinite ease-out',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)', opacity: 0.6 },
                      '100%': { transform: 'scale(1.8)', opacity: 0 },
                    },
                  }}
                />
                <Box
                  sx={{
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#e8f5e9',
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    border: '2px solid #fff',
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 60, color: '#2e7d32' }} />
                </Box>
              </Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 900, color: '#001665' }}
              >
                Verified
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#2e7d32',
                  fontWeight: 600,
                  bgcolor: '#e8f5e9',
                  px: 3,
                  py: 0.5,
                  borderRadius: '20px',
                }}
              >
                IDENTITY CONFIRMED
              </Typography>
              <Divider sx={{ width: '80%' }} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                You’re checked in! Your entry has already been authorized.
              </Typography>
            </Stack>
          )}

          {/* DENIED VIEW */}
          {status === 'denied' && (
            <Stack spacing={3} alignItems="center">
              <Box
                sx={{
                  bgcolor: '#fff1f0',
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BlockIcon sx={{ fontSize: 60, color: '#f5222d' }} />
              </Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 900, color: '#434343' }}
              >
                Access Denied
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#cf1322',
                  fontWeight: 700,
                  bgcolor: '#fff1f0',
                  px: 3,
                  py: 0.5,
                  borderRadius: '20px',
                  border: '1px solid #ffa39e',
                }}
              >
                SECURITY REJECTED
              </Typography>
              <Divider sx={{ width: '80%' }} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                The security desk has declined this request.
              </Typography>
            </Stack>
          )}

          {/* ERROR VIEW */}
          {status === 'error' && (
            <Stack spacing={3} alignItems="center">
              <SecurityIcon sx={{ fontSize: 60, color: '#8c8c8c' }} />
              <Typography variant="h4" sx={{ fontWeight: 900 }}>
                System Issue
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Unable to reach the gateway. Please refresh or visit the front
                desk.
              </Typography>
            </Stack>
          )}
        </Paper>
      </Fade>
    </Box>
  );
};

export default NfcGatePage;
